import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AmountInput from './AmountInput';
import EditableSparkline from './EditableSparkline';
import SeasonalityPicker from './SeasonalityPicker';
import { lineTotal } from './computeBillingKpis';
import { parseSwissAmount } from './formatSwiss';
import { scaleToTotal, coeffOfVariation } from './stats';
import {
  rotateValuesForDisplay,
  rotateValuesFromDisplay,
  displayIdxToCalendar,
} from './monthRotation';
import { rowColor } from './colors';
import {
  createVariableExpense, updateVariableExpense, deleteVariableExpense,
} from '../../../api/treasury';
import { useP } from '../../../i18n/useP';

// Draft/commit text input — no PATCH per keystroke.
function DraftTextInput({ value, onCommit, placeholder, className, disabled }) {
  const [draft, setDraft] = useState(null);
  return (
    <input
      type="text"
      className={className}
      placeholder={placeholder}
      disabled={disabled}
      value={draft !== null ? draft : (value || '')}
      onFocus={() => setDraft(value || '')}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null) {
          const trimmed = draft.trim();
          if (trimmed !== (value || '')) onCommit(trimmed);
        }
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.target.blur();
        if (e.key === 'Escape') { setDraft(null); e.target.blur(); }
      }}
    />
  );
}


// Focus panel : edit one revenue line at a time.
// This first step renders : title + subtitle + grand total + a 12-month
// grid of AmountInput. Sparkline (read-only then drag-to-edit) and
// seasonality preset buttons come in later steps.
function LockIcon({ locked }) {
  if (locked) {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </svg>
  );
}

export default function RevenueDetailPanel({
  revenue,
  colorIndex = 0,
  startMonth = 0,
  monthLabels,
  locked = false,
  showCogs = false,
  isTotal = false,
  allRevenues = [],
  vatRate = 0.081,
  variables = [],
  tenantId,
  scenarioId,
  onChangeVariables,
  onPatch,
  onDuplicate,
  onDelete,
}) {
  const { t } = useTranslation();
  const p = useP();
  const [totalLocked, setTotalLocked] = useState(false);
  const [cogsError, setCogsError] = useState(null);

  // Coût variable de la ligne = UNE charge pct_revenue liée à ce revenu
  // (ex-Section A des Achats, désormais éditée ici). Source de vérité = vc.
  const vc = revenue
    ? variables.find((v) => v.driver_type === 'pct_revenue' && v.revenue_ref_id === revenue.id) || null
    : null;
  const vcPct = vc ? Math.round(Number(vc.pct_rate || 0) * 10000) / 100 : 0;

  async function createVc(extra) {
    const r = await createVariableExpense(tenantId, scenarioId, {
      scenario_id: scenarioId,
      label: `${p('Coût variable —')} ${revenue.label || p('ligne')}`,
      driver_type: 'pct_revenue',
      revenue_ref_id: revenue.id,
      pct_rate: 0,
      payment_lag: 0,
      vat_recoverable: true,
      affects_cash: true,
      display_order: variables.length,
      ...extra,
    });
    if (r.error) setCogsError(r.error);
    else { setCogsError(null); onChangeVariables && onChangeVariables(); }
  }
  async function patchVc(payload) {
    const r = await updateVariableExpense(tenantId, scenarioId, vc.id, payload);
    if (r.error) setCogsError(r.error);
    else { setCogsError(null); onChangeVariables && onChangeVariables(); }
  }
  async function setVcPctFromInput(pctStr) {
    let pct = Number(pctStr);
    if (!Number.isFinite(pct) || pct < 0) pct = 0;
    const rate = pct / 100;
    if (vc) await patchVc({ pct_rate: rate });
    else if (rate > 0) await createVc({ pct_rate: rate });
  }

  if (!revenue) {
    return (
      <section className="billing-detail billing-detail--empty">
        <p>{p('Aucune ligne sélectionnée. Choisissez-en une dans la liste à gauche.')}</p>
      </section>
    );
  }

  const total = lineTotal(revenue);
  const monthlyValues = revenue.monthly_values || Array(12).fill(0);
  const displayValues = rotateValuesForDisplay(monthlyValues, startMonth);

  // Nature de la ligne, dérivée de goods_pct (0..1) : 0 prestation, 1 marchandise,
  // entre = mix. La pastille cycle Prestation → Marchandise → Mix → Prestation.
  const goodsPct = Number(revenue.goods_pct) || 0;
  const nature = goodsPct <= 0 ? 'prestation' : goodsPct >= 1 ? 'marchandise' : 'mix';
  const NATURE_META = {
    prestation:  { label: p('Prestation'),  color: '#5C7A3E' },
    marchandise: { label: p('Marchandise'), color: '#B8862A' },
    mix:         { label: p('Mix'),         color: '#2B203E' },
  };
  function cycleNature() {
    if (nature === 'prestation') onPatch({ goods_pct: 1 });      // → marchandise
    else if (nature === 'marchandise') onPatch({ goods_pct: 0.5 }); // → mix 50 %
    else onPatch({ goods_pct: 0 });                              // → prestation
  }
  function setGoodsPctFromInput(pctStr) {
    let pct = Math.round(Number(pctStr));
    if (!Number.isFinite(pct)) pct = 0;
    pct = Math.min(100, Math.max(0, pct));
    onPatch({ goods_pct: pct / 100 });
  }

  // Edit a single cell in display order : map to calendar index before patch.
  function patchMonth(displayIdx, nextValue) {
    const calIdx = displayIdxToCalendar(displayIdx, startMonth);
    const next = [...monthlyValues];
    next[calIdx] = nextValue;
    onPatch({ monthly_values: next });
  }

  // Collage Excel-like : distribue les valeurs collées sur les mois à partir de
  // la cellule (en ordre d'affichage), mappées vers l'ordre calendaire.
  function pasteMonths(displayStartIdx, tokens) {
    const next = [...monthlyValues];
    tokens.forEach((tok, k) => {
      const displayIdx = displayStartIdx + k;
      if (displayIdx > 11) return;
      const calIdx = displayIdxToCalendar(displayIdx, startMonth);
      next[calIdx] = parseSwissAmount(tok);
    });
    onPatch({ monthly_values: next });
  }

  function commitTotal(newTotal) {
    if (newTotal === total) return;
    // scaleToTotal is order-agnostic (proportional), can apply on calendar.
    onPatch({ monthly_values: scaleToTotal(monthlyValues, newTotal) });
  }

  // Sparkline commits in display order : rotate back to calendar for patch.
  function commitSparkline(nextDisplayValues) {
    onPatch({ monthly_values: rotateValuesFromDisplay(nextDisplayValues, startMonth) });
  }

  // Ventilation de la ligne « Total » (agrégat) : prestation / marchandise / coût
  // variable marchandise / TVA facturée / TVA nette sur marchandise. Calculée
  // depuis toutes les lignes de revenu et leurs coûts variables liés.
  const breakdown = isTotal ? (() => {
    const vatR = Number(vatRate) || 0;
    let presta = 0, march = 0, cogs = 0, vatFacturer = 0, vatDed = 0;
    for (const line of (allRevenues || [])) {
      const lineTot = lineTotal(line);
      const g = Math.min(1, Math.max(0, Number(line.goods_pct) || 0));
      const marchRev = lineTot * g;
      presta += lineTot * (1 - g);
      march += marchRev;
      const lineVc = variables.find((v) => v.driver_type === 'pct_revenue' && v.revenue_ref_id === line.id);
      const cost = marchRev * (lineVc ? Number(lineVc.pct_rate || 0) : 0);
      cogs += cost;
      if (line.vat_applicable) vatFacturer += lineTot * vatR;              // TVA collectée (output)
      if (lineVc && lineVc.vat_recoverable) vatDed += cost * vatR;   // TVA déductible sur coûts
    }
    return {
      presta, march, cogs,
      va: presta + march - cogs,          // valeur ajoutée
      vatFacturer,                        // TVA à facturer (en sus)
      vatPayer: vatFacturer - vatDed,     // TVA à payer = collectée − déductible
    };
  })() : null;
  const fmtCHF = (v) => `${Math.round(Number(v) || 0).toLocaleString('de-CH')} CHF`;

  return (
    <section className="billing-detail">
      <header className="billing-detail__header">
        <div className="billing-detail__left">
          <div className="billing-detail__title-block">
            <DraftTextInput
              className="billing-detail__title"
              value={revenue.label}
              placeholder={t('phrases:p_f5485bbae8')}
              disabled={locked}
              onCommit={(v) => onPatch({ label: v || p('Sans titre') })}
            />
            <DraftTextInput
              className="billing-detail__subtitle"
              value={revenue.detail || ''}
              placeholder={t('phrases:p_e4d82d4919')}
              disabled={locked}
              onCommit={(v) => onPatch({ detail: v || null })}
            />
          </div>

        <div className="billing-detail__mid">
        {!isTotal && (<div className="billing-detail__header-controls">
          <div className="billing-detail__nature">
            <button
              type="button"
              className="billing-detail__nature-pill"
              style={{ borderColor: NATURE_META[nature].color, color: NATURE_META[nature].color }}
              disabled={locked}
              onClick={cycleNature}
              title={p('Cliquer pour changer : Prestation → Marchandise → Mix')}
            >
              <span className="billing-detail__nature-dot" style={{ background: NATURE_META[nature].color }} />
              {NATURE_META[nature].label}
            </button>
          </div>
          <label className="billing-detail__control">
            <span>{p("Délai d'encaissement")}</span>
            <select
              value={revenue.payment_lag ?? 0}
              disabled={locked}
              onChange={(e) => onPatch({ payment_lag: Number(e.target.value) })}
            >
              {[0, 1, 2, 3].map((d) => (
                <option key={d} value={d}>{d} {p('mois')}</option>
              ))}
            </select>
          </label>
        </div>)}

        {isTotal && breakdown && (
          <div className="billing-detail__breakdown">
            <div className="billing-detail__bd-row"><span>{p('Prestations')}</span><b>{fmtCHF(breakdown.presta)}</b></div>
            <div className="billing-detail__bd-row"><span>{p('+ marchandises')}</span><b>{fmtCHF(breakdown.march)}</b></div>
            <div className="billing-detail__bd-row billing-detail__bd-row--neg"><span>{p('− coûts variables')}</span><b>{fmtCHF(-breakdown.cogs)}</b></div>
            <div className="billing-detail__bd-row billing-detail__bd-row--va"><span>{p('= Valeur ajoutée')}</span><b>{fmtCHF(breakdown.va)}</b></div>
          </div>
        )}

        {showCogs && goodsPct > 0 && (
          <div className="billing-detail__vc">
            <span className="billing-detail__vc-label">{p('Part de marchandises :')}</span>
            <span className="billing-detail__nature-pct">
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={Math.round(goodsPct * 100)}
                disabled={locked}
                onChange={(e) => setGoodsPctFromInput(e.target.value)}
                aria-label={p('Part de marchandises (%)')}
              />
              <span>%</span>
            </span>
            <span className="billing-detail__vc-text">{p('avec un coût direct de')}</span>
            <span className="billing-detail__nature-pct">
              <input
                type="number"
                min="0"
                step="1"
                value={vcPct}
                disabled={locked}
                onChange={(e) => setVcPctFromInput(e.target.value)}
                aria-label={t('phrases:p_356a6992b8')}
              />
              <span>%</span>
            </span>
            {cogsError && (
              <span className="billing-detail__vc-error">{cogsError}</span>
            )}
          </div>
        )}
        </div>
        </div>

        <div className="billing-detail__total">
          <span className="billing-detail__total-label">{p('Total Hors TVA')}</span>
          <AmountInput
            value={total}
            onCommit={commitTotal}
            disabled={locked}
            className="billing-detail__total-input"
            ariaLabel={t('phrases:p_3ab8de48be')}
          />
          {isTotal && breakdown && (
            <div className="billing-detail__vat-right">
              <div className="billing-detail__vat-row"><span>{p('TVA à facturer (en sus)')}</span><b>{fmtCHF(breakdown.vatFacturer)}</b></div>
              <div className="billing-detail__vat-row"><span>{p('TVA à payer (en sus)')}</span><b>{fmtCHF(breakdown.vatPayer)}</b></div>
            </div>
          )}
          {!isTotal && (<>
          <button
            type="button"
            className={`billing-detail__lock ${totalLocked ? 'billing-detail__lock--on' : ''}`}
            onClick={() => setTotalLocked((v) => !v)}
            disabled={locked}
            title={
              totalLocked
                ? p('Total verrouillé : le drag redistribue proportionnellement sur les autres mois.')
                : p('Total libre : le drag modifie la valeur sans redistribuer.')
            }
            aria-label={totalLocked ? t('phrases:p_c102e3b112') : p('Verrouiller le total')}
          >
            <LockIcon locked={totalLocked} />
            <span className="billing-detail__lock-label">
              {totalLocked ? p('Total verrouillé') : p('Total libre')}
            </span>
          </button>
          <label className="billing-detail__control billing-detail__control--check billing-detail__total-vat">
            <span>{p('Soumis TVA')}</span>
            <input
              type="checkbox"
              checked={!!revenue.vat_applicable}
              disabled={locked}
              onChange={(e) => onPatch({ vat_applicable: e.target.checked })}
            />
          </label>
          </>)}
        </div>
      </header>

      <EditableSparkline
        values={displayValues}
        color={rowColor(colorIndex)}
        monthLabels={monthLabels}
        disabled={locked}
        totalLocked={totalLocked}
        mode={nature}
        goodsPct={goodsPct}
        cogsRate={vc ? Number(vc.pct_rate || 0) : 0}
        onCommit={commitSparkline}
      />

      <p className="billing-detail__chart-hint">
        {p('Appliquez un profil saisonnier ou ajustez la courbe manuellement. Si vous changez uniquement la répartition mensuelle, veillez à figer le total')}
        <span className="billing-detail__chart-hint-lock"><LockIcon locked /></span>
      </p>

      {(() => {
        const cv = coeffOfVariation(monthlyValues);
        const warn = cv > 100;
        return (
          <div className={`billing-detail__cv ${warn ? 'billing-detail__cv--warn' : ''}`}>
            <span className="billing-detail__cv-value">{cv.toFixed(0)}%{warn ? ' ⚠' : ''}</span>
            <span className="billing-detail__cv-label">{p('coefficient de variation')}</span>
          </div>
        );
      })()}

      <SeasonalityPicker
        values={monthlyValues}
        currentTotal={total}
        disabled={locked}
        onApply={(calendarValues) => onPatch({ monthly_values: calendarValues })}
      />

      <div className="billing-detail__values">
        <div className="billing-detail__values-label">{p('Valeurs mensuelles HT (CHF)')}</div>
        <div className="billing-detail__values-grid">
          {displayValues.slice(0, 12).map((v, m) => (
            <label key={m} className="billing-detail__month">
              <span className="billing-detail__month-label">{monthLabels?.[m] ?? ''}</span>
              <AmountInput
                value={v}
                onCommit={(nextValue) => patchMonth(m, nextValue)}
                onPasteMulti={(tokens) => pasteMonths(m, tokens)}
                disabled={locked}
                ariaLabel={`${monthLabels?.[m] ?? ''} — ${revenue.label || p('ligne')}`}
              />
            </label>
          ))}
        </div>
      </div>

      {!locked && (
        <div className="billing-detail__actions">
          <button
            type="button"
            className="billing-detail__action billing-detail__action--dup"
            onClick={onDuplicate}
          >
            {t('common:actions.duplicate')}
          </button>
          <button
            type="button"
            className="billing-detail__action billing-detail__action--del"
            onClick={onDelete}
          >
            {p('Supprimer cette ligne')}
          </button>
        </div>
      )}
    </section>
  );
}
